import React, { createContext, useContext, useState } from 'react';
import { toast } from 'react-hot-toast';
import api from '../config/api';

const ProductContext = createContext();

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [phoneModels, setPhoneModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 12,
    totalPages: 0
  });

  const fetchProducts = async (params) => {
    try {
      setLoading(true);
      setError(null);

      // Sayfalama parametrelerini ekle
      const pageParams = {
        ...params,
        page: params?.page || 1,
        limit: params?.limit || 12
      };

      // Normal ürünleri getir
      const productsResponse = await api.get('/products', { params: pageParams });
      
      // Ürünleri ve sayfalama bilgilerini al
      const { products: fetchedProducts, pagination: paginationData } = productsResponse.data || { products: [], pagination: { total: 0, page: 1, limit: 12, totalPages: 0 } };
      
      let allProducts = [...(fetchedProducts || [])];
      
      // "Müşteri Tasarımları" koleksiyonu seçildiğinde VEYA arama yapıldığında özel tasarımları getir
      const selectedCollections = params?.collections ? params.collections.split(',') : [];
      const hasSearch = params?.search && params.search.trim().length > 0;
      
      if (selectedCollections.includes('Müşteri Tasarımları') || hasSearch) {
        // Özel tasarımları getir
        const customProductsResponse = await api.get('/products/custom');
        // Özel ürünleri al
        const customProducts = customProductsResponse.data || [];
        
        // Eğer arama yapıldıysa, custom products'ı da filtrele
        if (hasSearch) {
          const searchLower = params.search.toLowerCase();
          const filteredCustomProducts = customProducts.filter(product =>
            (product.name && product.name.toLowerCase().includes(searchLower)) ||
            (product.description && product.description.toLowerCase().includes(searchLower))
          );
          // Ürünleri birleştir
          allProducts = [...allProducts, ...(filteredCustomProducts || [])];
        } else {
          // Arama yoksa tüm custom products'ı ekle
          allProducts = [...allProducts, ...(customProducts || [])];
        }
      }
      
      // Null kontrolleri yap
      allProducts = allProducts.filter(product => product && product.id);
      
      // Filtreleri uygula
      let filteredProducts = allProducts;
      
      // Koleksiyon filtresi
      if (params?.collections) {
        const selectedCollections = params.collections.split(',');
        filteredProducts = filteredProducts.filter(product => 
          product.collections && selectedCollections.includes(product.collections.name)
        );
      }
      
      // Telefon modeli filtresi artık backend'te yapılıyor
      // Frontend'te tekrar filtrelemeye gerek yok
      
      // Fiyat filtresi
      if (params?.min_price) {
        filteredProducts = filteredProducts.filter(product => 
          product.price && product.price >= parseFloat(params.min_price)
        );
      }
      if (params?.max_price) {
        filteredProducts = filteredProducts.filter(product => 
          product.price && product.price <= parseFloat(params.max_price)
        );
      }
      
      // Arama filtresi
      if (params?.search) {
        const searchLower = params.search.toLowerCase();
        filteredProducts = filteredProducts.filter(product =>
          (product.name && product.name.toLowerCase().includes(searchLower)) ||
          (product.description && product.description.toLowerCase().includes(searchLower))
        );
      }
      
      // Sıralama
      if (params?.sort) {
        switch (params.sort) {
          case 'price-asc':
            filteredProducts.sort((a, b) => (a.price || 0) - (b.price || 0));
            break;
          case 'price-desc':
            filteredProducts.sort((a, b) => (b.price || 0) - (a.price || 0));
            break;
          case 'newest':
            filteredProducts.sort((a, b) => {
              const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
              const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
              return dateB - dateA;
            });
            break;
          default:
            break;
        }
      }

      // Sayfa 1'den büyükse ve daha önce ürünler yüklenmişse, ürünleri birleştir
      if (params?.page > 1 && params?.appendResults) {
        setProducts(prevProducts => [...prevProducts, ...filteredProducts]);
      } else {
        setProducts(filteredProducts);
      }
      
      // Sayfalama bilgilerini güncelle
      setPagination(paginationData);
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Ürünler yüklenirken bir hata oluştu');
      toast.error('Ürünler yüklenirken bir hata oluştu. Lütfen sayfayı yenileyin.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPhoneModels = async () => {
    try {
      const { data } = await api.get('/phone-models');
      const activeModels = data.filter(model => model && model.is_active);
      setPhoneModels(activeModels);
      setError(null);
    } catch (error) {
      console.error('Telefon modelleri yüklenirken hata:', error);
      setError('Telefon modelleri yüklenemedi');
      toast.error('Telefon modelleri yüklenirken bir hata oluştu');
    }
  };

  return (
    <ProductContext.Provider value={{
      products,
      phoneModels,
      loading,
      error,
      pagination,
      fetchProducts,
      fetchPhoneModels
    }}>
      {children}
    </ProductContext.Provider>
  );
};

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProducts hook must be used within a ProductProvider');
  }
  return context;
};

export default ProductContext;